import {MouseEvent, useCallback} from 'react';
import {NextProblemEntryEnum} from '@types';
import {
    useFetchNextProblemPostMutation,
    useStartSrsSessionMutation,
} from '@api';
import {buildNextProblemPath, useDispatch} from '@utils';
import {useNavigate} from 'react-router';
import {uiSlice} from '@reducers';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';

export const useHeaderNavigation = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [fetchNextProblemPost] = useFetchNextProblemPostMutation();
    const [startSrsSession] = useStartSrsSessionMutation();
    const handleAutolevelClick = useCallback(
        async (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();
            try {
                const result = await fetchNextProblemPost({
                    name: NextProblemEntryEnum.AUTOLEVEL,
                }).unwrap();

                if (result.nextProblem) {
                    navigate(
                        buildNextProblemPath(
                            result.nextProblem.id,
                            result.argument
                        )
                    );
                }
            } catch (error) {
                console.error('Error fetching autolevel problem:', error);
            }
        },
        [fetchNextProblemPost, navigate]
    );

    const handleTrainingClick = useCallback(
        async (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();
            try {
                const session = await startSrsSession().unwrap();
                const problemIds = session?.remainingProblemIds ?? [];
                dispatch(uiSlice.actions.restoreTrainingSession(problemIds));

                const firstProblemId = problemIds[0];
                if (firstProblemId === undefined) {
                    navigate('/training/cooldown');
                    return;
                }

                navigate(
                    buildNextProblemPath(firstProblemId, {
                        name: NextProblemEntryEnum.TRAINING,
                    })
                );
            } catch (error) {
                console.error('Unable to start SRS training:', error);
                toast.error(t('training.error_loading_data'));
            }
        },
        [dispatch, navigate, startSrsSession, t]
    );

    return {
        handleAutolevelClick,
        handleTrainingClick,
    };
};
